import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { Button, Select, Option } from 'src/components/PulseUI';

const DEFAULT_PAGE_SIZE_OPTIONS = [50, 75, 100];

interface DataTablePaginationProps {
  count: number;
  sortValues: {
    page: number;
    pageSize: number;
  };
  setSortValues: React.Dispatch<
    React.SetStateAction<{
      page: number;
      pageSize: number;
    }>
  >;
  /**
   * Page sizes offered in the "Items per page" select.
   * @default [50, 75, 100]
   */
  pageSizeOptions?: number[];
}

/**
 * DataTablePagination - A pagination component for data tables
 *
 * @description A pagination component that provides page navigation, page size selection,
 * and full accessibility compliance for data tables.
 */
const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  count,
  sortValues,
  setSortValues,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}) => {
  const totalPages = Math.ceil(count / sortValues.pageSize);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  const maxVisiblePages = 5;

  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) return pageNumbers;

    const start = Math.max(
      1,
      sortValues.page - Math.floor(maxVisiblePages / 2)
    );
    const end = Math.min(totalPages, start + maxVisiblePages - 1);

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handlePageSizeChange = (
    event: React.MouseEvent,
    value: string | number
  ) => {
    if (value) {
      setSortValues({
        ...sortValues,
        pageSize: Number(value),
        page: 1, // Reset to first page when changing page size
      });
    }
  };

  const handlePageChange = (page: number) => {
    setSortValues({
      ...sortValues,
      page,
    });
  };

  const handlePreviousPage = () => {
    if (sortValues.page > 1) {
      handlePageChange(sortValues.page - 1);
    }
  };

  const handleNextPage = () => {
    if (sortValues.page < totalPages) {
      handlePageChange(sortValues.page + 1);
    }
  };

  return (
    <nav
      className="flex items-center justify-between gap-2 px-4 py-2 bg-white border-t border-neutral-200"
      role="navigation"
      aria-label="Table pagination"
    >
      <div className="text-sm text-neutral-600" aria-live="polite">
        {count > 0 ? (
          <>
            Showing {(sortValues.page - 1) * sortValues.pageSize + 1} to{' '}
            {Math.min(sortValues.page * sortValues.pageSize, count)} of {count}{' '}
            results
          </>
        ) : (
          'No results'
        )}
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2">
          <label
            htmlFor="page-size-select"
            className="text-sm text-neutral-600"
          >
            Items per page:
          </label>
          <Select
            id="page-size-select"
            size="sm"
            variant="outlined"
            className="rounded-full"
            value={sortValues.pageSize}
            onChange={handlePageSizeChange}
            aria-label="Select number of items per page"
          >
            {pageSizeOptions.map((pageSize) => (
              <Option key={pageSize} value={pageSize}>
                {pageSize}
              </Option>
            ))}
          </Select>
        </div>

        <div
          className="flex items-center gap-1"
          role="group"
          aria-label="Page navigation"
        >
          <Button
            variant="plain"
            color="neutral"
            className="p-1"
            onClick={handlePreviousPage}
            disabled={sortValues.page === 1}
            aria-label={`Go to previous page, page ${sortValues.page - 1}`}
            title="Previous page"
            data-testid={`go-to-previous-page-page-${sortValues.page - 1}`}
          >
            <Icon icon={faChevronLeft} className="w-4 h-4" aria-hidden="true" />
          </Button>

          {getVisiblePages().map((page) => (
            <Button
              key={page}
              variant="plain"
              className={`w-8 h-8 text-sm rounded-full transition-colors duration-150 ${
                page === sortValues.page
                  ? 'bg-sky-600 text-white'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
              onClick={() => handlePageChange(page)}
              aria-label={`Go to page ${page}`}
              aria-current={page === sortValues.page ? 'page' : undefined}
              title={`Page ${page}`}
              data-testid={`go-to-page-${page}`}
            >
              {page}
            </Button>
          ))}

          <Button
            variant="plain"
            color="neutral"
            className="p-1"
            onClick={handleNextPage}
            disabled={sortValues.page === totalPages}
            aria-label={`Go to next page, page ${sortValues.page + 1}`}
            title="Next page"
            data-testid={`go-to-next-page-page-${sortValues.page + 1}`}
          >
            <Icon
              icon={faChevronRight}
              className="w-4 h-4"
              aria-hidden="true"
            />
          </Button>
        </div>
      </div>
    </nav>
  );
};

export default DataTablePagination;
